// 마스터 유저관리 endpoint 호출.
// - 화주찾기/부서찾기/이메일·휴대폰 중복확인: 머지된 /api/v1/auth/* 재사용 (8888/8746/8744/8745).
//   signup 과 달리 마스터는 로그인 상태라 noAuth 미사용(일반 인증 호출).
// - 유저 목록/탭값/액션: LOGISPRD-8899/8889 (BE 진행 중).
// - 유저/화주 수정: LOGISPRD-9079 (BE 진행 중).
import request from '@/utils/request'
import { SHIPPER_TYPES } from '@/services/shipper/apis'
import {
  ApproveUserReq,
  BulkActionRes,
  DeleteActionRes,
  GetMasterShippersReq,
  GetMasterShippersRes,
  GetMasterUsersReq,
  GetMasterUsersRes,
  GetMasterUserTabCountsReq,
  GetMasterUserTabCountsRes,
  CheckPhoneDuplicateReq,
  CheckPhoneDuplicateRes,
  RejectUserReq,
  UpdateAuthCodeReq,
  UpdateShipperGroupReq,
  UpdateShipperMultiGroupReq,
  UpdateUserProfileReq,
  UserActionRes,
} from './types'

export const PAGE_SIZE_ON_USER_MANAGEMENT = 100

const BASE_URL = '/api/v1/master/users'

// 화주 드롭다운 옵션 — 등록 화주 전체 {value: 화주코드, label: 화주명}.
// 화주명이 유니크하지 않아 코드로 필터하므로, 옵션에 화주코드를 함께 싣는다.
export const getMasterShipperOptions = (): Promise<{ value: string; label: string }[]> =>
  request
    .post<ApiResponseV2<{ items: { shipperCode: string; shipperName: string }[] }>, { logisticsServiceTypes: string[] }>(
      '/api/v1/shipper-filter-options',
      { logisticsServiceTypes: [...SHIPPER_TYPES] },
    )
    .then((r) => (r.data?.items ?? []).map((s) => ({ value: s.shipperCode, label: s.shipperName })))

// 화주(업체) 목록 — 상단 그리드. master_shipper 기반, 화주명 다중 / 이메일(Okta ID) 필터 (LOGISPRD-9131).
// 응답에 멀티화주그룹·부서목록 포함. email 지정 시 해당 유저가 속한 화주를 반환.
export const getMasterShippers = (req: GetMasterShippersReq): Promise<GetMasterShippersRes> =>
  request.get<GetMasterShippersRes, GetMasterShippersReq>('/api/v1/master/shippers', {
    ...req,
    size: req.size ?? PAGE_SIZE_ON_USER_MANAGEMENT,
  })

// 부서 찾기 — 선택 화주의 부서 목록 (LOGISPRD-8746 `/api/v1/auth/departments`)
export const getMasterDepartments = async (shipperCode: string): Promise<string[]> => {
  if (!shipperCode) return []
  const res = await request.get<ApiResponseV2<{ items: string[] }>, { shipperCode: string }>('/api/v1/auth/departments', { shipperCode })
  return res.data?.items ?? []
}

// 권한 찾기 — 선택 화주의 권한 목록 (LOGISPRD-9313 `/api/v1/auth/codes`). MOMS_AUTH_INFO 기반 화주별 권한.
export const getMasterAuthCodes = async (shipperCode: string): Promise<{ authCode: string; authName: string }[]> => {
  if (!shipperCode) return []
  const res = await request.get<ApiResponseV2<{ items: { authCode: string; authName: string }[] }>, { shipperCode: string }>('/api/v1/auth/codes', {
    shipperCode,
  })
  return res.data?.items ?? []
}

// 1. 유저 목록 조회 (4탭 공통) — LOGISPRD-8899
export const getMasterUsers = (req: GetMasterUsersReq): Promise<GetMasterUsersRes> =>
  request.get<GetMasterUsersRes, GetMasterUsersReq>(BASE_URL, { ...req, size: req.size ?? PAGE_SIZE_ON_USER_MANAGEMENT })

// 탭별 건수 — 탭 라벨 카운트 (BE 협의 필요 — LOGISPRD-8899 spec 외 신규)
export const getMasterUserTabCounts = (req: GetMasterUserTabCountsReq): Promise<GetMasterUserTabCountsRes> =>
  request.get<GetMasterUserTabCountsRes, GetMasterUserTabCountsReq>(`${BASE_URL}/tab-counts`, req)

// 2. 가입 승인 (권한 부여 + Okta createUser 연동)
export const approveUser = (id: number, body: ApproveUserReq): Promise<UserActionRes> =>
  request.post<UserActionRes, ApproveUserReq>(`${BASE_URL}/${id}/approve`, body)

// 3. 가입 반려
export const rejectUser = (id: number, body: RejectUserReq): Promise<UserActionRes> =>
  request.post<UserActionRes, RejectUserReq>(`${BASE_URL}/${id}/reject`, body)

// 4~7. 벌크 액션 — BE(LOGISPRD-8889)는 컬렉션 벌크(`{ ids }`, max 100) + 부분성공 응답
export const activateUsers = (ids: number[]): Promise<BulkActionRes> =>
  request.post<BulkActionRes, { ids: number[] }>(`${BASE_URL}/activate`, { ids })

export const deactivateUsers = (ids: number[]): Promise<BulkActionRes> =>
  request.post<BulkActionRes, { ids: number[] }>(`${BASE_URL}/deactivate`, { ids })

export const resendInvites = (ids: number[]): Promise<BulkActionRes> =>
  request.post<BulkActionRes, { ids: number[] }>(`${BASE_URL}/resend-invite`, { ids })

export const resetPasswords = (ids: number[]): Promise<BulkActionRes> =>
  request.post<BulkActionRes, { ids: number[] }>(`${BASE_URL}/reset-password`, { ids })

// Spec: docs/spec/master/SPEC-master-user-mfa-reset-A.yaml#mfa-reset-posts-selected-ids
export const resetMfa = (ids: number[]): Promise<BulkActionRes> => request.post<BulkActionRes, { ids: number[] }>(`${BASE_URL}/reset-mfa`, { ids })

// 8. 권한 변경
export const updateAuthCode = (id: number, body: UpdateAuthCodeReq): Promise<UserActionRes> =>
  request.put<UserActionRes, UpdateAuthCodeReq>(`${BASE_URL}/${id}/auth-code`, body)

// 9. 화주·부서·멀티화주그룹 편집
export const updateShipperGroup = (id: number, body: UpdateShipperGroupReq): Promise<UserActionRes> =>
  request.put<UserActionRes, UpdateShipperGroupReq>(`${BASE_URL}/${id}/shipper-group`, body)

// 10. 사용자 삭제 (LOGISPRD-9315 soft delete — 벌크 POST /delete {ids})
export const deleteUsers = (ids: number[]): Promise<DeleteActionRes> =>
  request.post<DeleteActionRes, { ids: number[] }>(`${BASE_URL}/delete`, { ids })

// 11. 유저 프로필 인라인 수정 (이름/휴대폰/원화주명 등 — BE 협의 필요)
export const updateUserProfile = (id: number, body: UpdateUserProfileReq): Promise<UserActionRes> =>
  request.put<UserActionRes, UpdateUserProfileReq>(`${BASE_URL}/${id}/profile`, body)

// 12. 휴대폰번호 중복 확인 — 본인 제외(excludeUserId) 지원. {duplicated} 반환.
export const checkPhoneDuplicate = (params: CheckPhoneDuplicateReq): Promise<CheckPhoneDuplicateRes> =>
  request.get<CheckPhoneDuplicateRes, { phone: string; excludeUserId?: number }>(`${BASE_URL}/check-phone`, {
    phone: params.phone,
    excludeUserId: params.excludeUserId,
  })

// 13. 멀티화주그룹 저장 — 상단 화주 그리드 멀티화주그룹 모달
export const updateShipperMultiGroup = (shipperCode: string, body: UpdateShipperMultiGroupReq): Promise<UserActionRes> =>
  request.put<UserActionRes, UpdateShipperMultiGroupReq>(`/api/v1/master/shippers/${shipperCode}/multi-group`, body)

// 14. 화주 사용여부 변경 — 상단 화주 그리드 (LOGISPRD-9330: master_shipper.use_flag + ShipperInfoEvent(UPDATE))
export const setMasterShipperUseFlag = (shipperCode: string, use: boolean): Promise<UserActionRes> =>
  request.put<UserActionRes, { use: boolean }>(`/api/v1/master/shippers/${shipperCode}/use-flag`, { use })

// 15. 부서 추가 — 선택 화주 하위에 부서 등록 (LOGISPRD-9391: MOMS_DEPT_INFO)
export const addMasterDepartment = (shipperCode: string, departmentName: string, manager: string): Promise<UserActionRes> =>
  request.post<UserActionRes, { shipperCode: string; departmentName: string; manager: string }>('/api/v1/master/departments', {
    shipperCode,
    departmentName,
    manager,
  })
